<template>
    <section>
        <!-- Paged actions or a trace toncenter refused to send whole: the balances
             of the accounts down the chain are not on the page, so there is no flow. -->
        <div v-if="isUnavailable" class="tx-history-empty-panel">
            {{ $t('valueFlow.large_trace') }}
        </div>

        <div v-else-if="isReady && rows.length === 0" class="tx-history-empty-panel">
            {{ $t('valueFlow.empty') }}
        </div>

        <div v-else class="tx-history-wrap">
            <table class="ui-table value-flow-table">
                <thead>
                    <tr>
                        <th v-text="$t('valueFlow.account')"/>
                        <th class="ui-table__cell--align-right" v-text="$t('valueFlow.balance_change')"/>
                        <th class="ui-table__cell--align-right"
                            v-for="asset in assets"
                            v-bind:key="`value_flow_head_${asset.address}`"
                            v-bind:title="asset.friendly">
                            {{ asset.symbol || shortAddress(asset.friendly) }}
                        </th>
                        <th class="ui-table__cell--align-right" v-text="$t('valueFlow.fee')"/>
                    </tr>
                </thead>

                <tbody v-if="!isReady">
                    <tr v-for="i in 3" v-bind:key="`value_flow_skeleton_${i}`">
                        <td v-pre><span class="skeleton">SomeCoolBouncableAddress</span></td>
                        <td v-pre class="ui-table__cell--align-right"><span class="skeleton">+0.000000 GRAM</span></td>
                        <td v-pre class="ui-table__cell--align-right"><span class="skeleton">0.000000 GRAM</span></td>
                    </tr>
                </tbody>

                <tbody v-else>
                    <tr v-for="row in rows" v-bind:key="`value_flow_row_${row.address}`">
                        <td>
                            <!-- The diagram below lights up this account's nodes while the row is hovered,
                                 the same way the overview rows do it: -->
                            <ui-address
                                v-bind:address="row.friendly"
                                v-on:mouseenter.native="highlightNode(row.friendly)"
                                v-on:mouseleave.native="clearHighlightNode"/>
                        </td>
                        <td class="ui-table__cell--align-right value-flow-table__amount"
                            v-bind:class="amountClass(row.change)">
                            {{ signed(row.change, 9) }} GRAM
                        </td>
                        <td class="ui-table__cell--align-right value-flow-table__amount"
                            v-for="(delta, i) in row.changes"
                            v-bind:key="`value_flow_cell_${row.address}_${assets[i].address}`"
                            v-bind:class="amountClass(delta)">
                            <template v-if="hasChange(delta)">
                                {{ signed(delta, assets[i].decimals) }}
                                <span class="value-flow-table__symbol" v-if="assets[i].symbol" v-text="assets[i].symbol"/>
                            </template>
                            <span v-else class="muted">&mdash;</span>
                        </td>
                        <td class="ui-table__cell--align-right value-flow-table__amount muted">
                            {{ $ton(Number(row.fee)) }} GRAM
                        </td>
                    </tr>
                </tbody>

                <!-- The label sits in the columns to the left, so that the fee column
                     stays as narrow as its numbers: -->
                <tfoot v-if="isReady && rows.length > 1">
                    <tr>
                        <td class="ui-table__cell--align-right value-flow-table__total muted"
                            v-bind:colspan="2 + assets.length"
                            v-text="$t('valueFlow.total_fee')"/>
                        <td class="ui-table__cell--align-right value-flow-table__amount value-flow-table__total">
                            {{ $ton(Number(totalFee)) }} GRAM
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </section>
</template>

<script>
import { buildValueFlow } from '~/valueFlow.js';

export default {
    props: {
        // Trace transactions keyed by hash, as toncenter returns them:
        transactions: Object,
        // Trace actions -- the raw toncenter shape, with or without the previews on top:
        actions: Array,
        addressBook: Object,
        metadata: Object,
        isLargeTrace: Boolean,
        warning: Boolean,
    },

    computed: {
        isUnavailable() {
            return this.isLargeTrace || this.warning;
        },

        isReady() {
            return !!this.transactions && !!this.actions;
        },

        flow() {
            if (!this.isReady) {
                return { rows: [], assets: [], totalFee: 0n };
            }

            return buildValueFlow({
                transactions: this.transactions,
                actions: this.actions,
                addressBook: this.addressBook,
                metadata: this.metadata,
            });
        },

        rows() {
            return this.flow.rows;
        },

        assets() {
            return this.flow.assets;
        },

        totalFee() {
            return this.flow.totalFee;
        },
    },

    methods: {
        // A balance change with its sign spelled out, zero as a plain 0. The
        // formatter works on Numbers: past 2^53 nanotons the last digits go,
        // which is as much as the rest of the site shows anyway.
        signed(value, decimals) {
            if (value === undefined || value === 0n) {
                return '0';
            }

            const sign = value < 0n ? '−' : '+';
            const abs = value < 0n ? -value : value;

            return sign + this.$ton(Number(abs), decimals);
        },

        hasChange(value) {
            return value !== undefined && value !== 0n;
        },

        amountClass(value) {
            if (value === undefined || value === 0n) {
                return undefined;
            }

            return value > 0n
                ? 'value-flow-table__amount--in'
                : 'value-flow-table__amount--out';
        },

        shortAddress(address) {
            return `${address.slice(0, 6)}…${address.slice(-4)}`;
        },

        highlightNode(address) {
            if (address) this.$store.commit('setHighlightedNode', { address });
        },

        clearHighlightNode() {
            this.$store.commit('clearHighlightedNode');
        },
    },
};
</script>

<style lang="scss">
.value-flow-table {
    // A jetton column per token: tighter cells than the stock table, so that
    // a couple of them still fit next to the balance and the fee.
    th, td {
        padding-left: 10px;
        padding-right: 10px;
        &:first-child { padding-left: 18px; }
        &:last-child { padding-right: 18px; }
    }
    // The address column takes whatever the numbers leave and truncates in
    // it (max-width: 0 is what lets a table cell shrink below its content):
    th:first-child, td:first-child {
        width: 100%;
        max-width: 0;
        min-width: 110px;
    }
    &__amount {
        font-feature-settings: 'tnum';
        white-space: nowrap;
        &--in {
            color: var(--badge-green-color);
        }
    }
    &__symbol {
        margin-left: 4px;
        opacity: .5;
    }
    &__total {
        font-weight: 500;
    }
    tfoot td {
        border-top: 1px solid var(--card-border-color);
    }
}
</style>
